import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material';
import { Receipt, Add, Edit, Delete, UploadFile, AutoAwesome, PictureAsPdf } from '@mui/icons-material';
import ImportExportBar from '../../components/ImportExport/ImportExportBar';
import { emitDataUpdated } from '../../utils/dataBus';
import { logAuditEvent } from '../../utils/audit';
import modeleData from './modeles/ecritures_modeles.json';
import { generateNextReference, loadEntiteSettings } from '../../utils/numbering';
import { PDFDocument, StandardFonts } from 'pdf-lib';

interface Ligne {
  numero_compte: string;
  libelle: string;
  debit: number;
  credit: number;
}

interface Ecriture {
  id: string;
  date_ecriture: string; // ISO
  journal: string; // ACH, VTE, BQ, CAI, OD
  libelle: string;
  reference?: string;
  pj_url?: string;
  montant_total: number;
  statut: 'brouillard' | 'valide' | 'annule';
  lignes: Ligne[];
}

const STORAGE_KEY = 'comptaEBNL_ecritures_local';

const Ecritures: React.FC = () => {
  const [ecritures, setEcritures] = useState<Ecriture[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Ecriture | null>(null);

  const [form, setForm] = useState<Ecriture>({
    id: '',
    date_ecriture: new Date().toISOString().slice(0, 10),
    journal: 'ACH',
    libelle: '',
    reference: generateNextReference(new Date().toISOString().slice(0,10)),
    montant_total: 0,
    statut: 'brouillard',
    lignes: [
      { numero_compte: '606', libelle: 'Fournitures', debit: 0, credit: 0 },
      { numero_compte: '401', libelle: 'Fournisseur', debit: 0, credit: 0 },
    ],
  });
  const [modelId, setModelId] = useState<string>('');
  const [pjFile, setPjFile] = useState<File | null>(null);
  const applyModel = (id: string) => {
    const mdl = (modeleData as any[]).find(m => m.id === id);
    if (!mdl) return;
    // simple template replacement
    const montant = 1000; const tva = Math.round(montant * 0.18 * 100)/100; const montant_ttc = montant + tva; const salaires_bruts = 1000; const cotisations = 200; const salaires_nets = 800; const valeur = 500;
    const vars: Record<string, number> = { montant, tva, montant_ttc, salaires_bruts, cotisations, salaires_nets, valeur };
    const lignes = mdl.lignes.map((l: any) => ({
      numero_compte: String(l.numero_compte),
      libelle: l.libelle || '',
      debit: typeof l.debit === 'string' && l.debit.startsWith('{') ? (vars[l.debit.replace(/[{}]/g,'')] || 0) : Number(l.debit) || 0,
      credit: typeof l.credit === 'string' && l.credit.startsWith('{') ? (vars[l.credit.replace(/[{}]/g,'')] || 0) : Number(l.credit) || 0,
    }));
    setForm(f => ({ ...f, libelle: mdl.label, lignes }));
  };

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        setEcritures(JSON.parse(raw));
      } catch {
        setEcritures([]);
      }
    }
  }, []);

  const saveAll = (items: Ecriture[]) => {
    setEcritures(items);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    emitDataUpdated();
    logAuditEvent({ module: 'ecritures', action: 'save_all', details: { count: items.length } });
  };

  const handleNew = () => {
    setEditing(null);
    setForm({
      id: '',
      date_ecriture: new Date().toISOString().slice(0, 10),
      journal: 'ACH',
      libelle: '',
      reference: generateNextReference(new Date().toISOString().slice(0,10)),
      montant_total: 0,
      statut: 'brouillard',
      lignes: [
        { numero_compte: '606', libelle: 'Fournitures', debit: 0, credit: 0 },
        { numero_compte: '401', libelle: 'Fournisseur', debit: 0, credit: 0 },
      ],
    });
    setOpen(true);
    logAuditEvent({ module: 'ecritures', action: 'new_click' });
  };

  const handleEdit = (e: Ecriture) => {
    setEditing(e);
    setForm({ ...e });
    setOpen(true);
  };

  const handleDelete = (id: string) => {
    const next = ecritures.filter(e => e.id !== id);
    saveAll(next);
    logAuditEvent({ module: 'ecritures', action: 'delete', details: { id } });
  };

  const totalLignes = useMemo(() => {
    const debit = form.lignes.reduce((s, l) => s + (Number(l.debit) || 0), 0);
    const credit = form.lignes.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    return { debit, credit };
  }, [form.lignes]);

  useEffect(() => {
    setForm(f => ({ ...f, montant_total: totalLignes.debit || totalLignes.credit }));
  }, [totalLignes.debit, totalLignes.credit]);

  const upsert = () => {
    const id = editing?.id || `${Date.now()}`;
    const payload: Ecriture = { ...form, id };
    let next = ecritures.slice();
    const idx = next.findIndex(e => e.id === id);
    if (idx >= 0) next[idx] = payload; else next.unshift(payload);
    saveAll(next);
    try {
      const s = loadEntiteSettings();
      if (s.autoGenerateFiche) {
        // trigger same button logic silently
        (async () => {
          const pdfDoc = await PDFDocument.create();
          const page = pdfDoc.addPage([595, 842]);
          const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
          const { width, height } = page.getSize();
          const draw = (text: string, x: number, y: number, size = 10) => page.drawText(text, { x, y, size, font });
          draw('FICHE D\'IMPUTATION', 200, height - 50, 14);
          draw(`Référence: ${payload.reference || ''}`, 40, height - 90);
          draw(`Date: ${payload.date_ecriture}`, 40, height - 110);
          draw(`Journal: ${payload.journal}`, 40, height - 130);
          draw(`Libellé: ${payload.libelle}`, 40, height - 150);
          let y = height - 180;
          payload.lignes.forEach((l, i) => {
            draw(`${i+1}. ${l.numero_compte} - ${l.libelle}  D: ${l.debit}  C: ${l.credit}`, 40, y);
            y -= 16;
          });
          const bytes = await pdfDoc.save();
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = `Fiche_Imputation_${payload.reference || 'ref'}.pdf`; a.click(); URL.revokeObjectURL(url);
        })();
      }
    } catch {}
    setOpen(false);
    logAuditEvent({ module: 'ecritures', action: editing ? 'update' : 'create', details: { id } });
  };

  const updateLigne = (index: number, patch: Partial<Ligne>) => {
    const next = form.lignes.slice();
    next[index] = { ...next[index], ...patch };
    setForm({ ...form, lignes: next });
  };

  const addLigne = () => {
    setForm({ ...form, lignes: [...form.lignes, { numero_compte: '', libelle: '', debit: 0, credit: 0 }] });
  };

  const removeLigne = (index: number) => {
    const next = form.lignes.slice();
    next.splice(index, 1);
    setForm({ ...form, lignes: next });
  };

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length === 0) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const cols = line.split(',');
      const row: Record<string, string> = {};
      headers.forEach((h, i) => { row[h] = (cols[i] || '').trim(); });
      return row;
    });
  };

  const onImportCSV = (csvText: string) => {
    // Expected columns: id,date_ecriture,journal,libelle,statut,numero_compte,ligne_libelle,debit,credit
    const rows = parseCSV(csvText);
    const grouped: Record<string, Ecriture> = {};
    for (const r of rows) {
      const id = r.id || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      if (!grouped[id]) {
        grouped[id] = {
          id,
          date_ecriture: r.date_ecriture || new Date().toISOString().slice(0, 10),
          journal: (r.journal || 'OD') as any,
          libelle: r.libelle || '',
          montant_total: 0,
          statut: (r.statut as any) || 'brouillard',
          lignes: [],
        };
      }
      grouped[id].lignes.push({
        numero_compte: r.numero_compte || '',
        libelle: r.ligne_libelle || '',
        debit: Number(r.debit) || 0,
        credit: Number(r.credit) || 0,
      });
    }
    const list = Object.values(grouped).map(e => ({
      ...e,
      montant_total: Math.max(
        e.lignes.reduce((s, l) => s + (Number(l.debit) || 0), 0),
        e.lignes.reduce((s, l) => s + (Number(l.credit) || 0), 0)
      ),
    }));
    saveAll(list);
    logAuditEvent({ module: 'ecritures', action: 'import_csv', details: { count: list.length } });
  };

  const onImportJSON = (jsonText: string) => {
    try {
      const data = JSON.parse(jsonText);
      if (Array.isArray(data)) {
        saveAll(data as Ecriture[]);
        logAuditEvent({ module: 'ecritures', action: 'import_json', details: { count: data.length } });
      }
    } catch {}
  };

  const getExportCSV = () => {
    const header = 'id,date_ecriture,journal,libelle,statut,numero_compte,ligne_libelle,debit,credit';
    const lines: string[] = [header];
    ecritures.forEach(e => {
      e.lignes.forEach(l => {
        lines.push([
          e.id,
          e.date_ecriture,
          e.journal,
          e.libelle.replace(/,/g, ' '),
          e.statut,
          l.numero_compte,
          (l.libelle || '').replace(/,/g, ' '),
          (l.debit || 0).toString(),
          (l.credit || 0).toString(),
        ].join(','));
      });
    });
    return { filename: 'ecritures.csv', content: lines.join('\n') };
  };

  const getExportJSON = () => ({ filename: 'ecritures.json', content: JSON.stringify(ecritures, null, 2) });
  // audit on export will be triggered by button click via this getter usage context

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Écritures Comptables
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Gestion des écritures et des journaux comptables (mode local)
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt color="primary" />
              <Typography variant="h6">Liste des écritures</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField select size="small" label="Modèle" value={modelId} onChange={e => { setModelId(e.target.value); applyModel(e.target.value); }}>
                <MenuItem value="">Aucun</MenuItem>
                {(modeleData as any[]).map(m => (<MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>))}
              </TextField>
              <ImportExportBar onImportCSV={onImportCSV} onImportJSON={onImportJSON} getExportCSV={getExportCSV} getExportJSON={getExportJSON} />
              <Button variant="contained" startIcon={<Add />} onClick={handleNew}>
                Nouvelle écriture
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Journal</TableCell>
              <TableCell>Libellé</TableCell>
              <TableCell align="right">Montant</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ecritures.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">Aucune écriture</TableCell></TableRow>
            ) : ecritures.map(e => (
              <TableRow key={e.id} hover>
                <TableCell>{e.date_ecriture}</TableCell>
                <TableCell>{e.journal}</TableCell>
                <TableCell>{e.libelle}</TableCell>
                <TableCell align="right">{e.montant_total.toFixed(2)} €</TableCell>
                <TableCell>
                  <Chip size="small" label={e.statut} color={e.statut === 'valide' ? 'success' : e.statut === 'brouillard' ? 'default' : 'warning'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(e)}><Edit fontSize="small"/></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(e.id)}><Delete fontSize="small"/></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {open && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {editing ? 'Modifier une écriture' : 'Nouvelle écriture'}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  value={form.date_ecriture}
                  onChange={e => setForm({ ...form, date_ecriture: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Journal"
                  value={form.journal}
                  onChange={e => setForm({ ...form, journal: e.target.value })}
                >
                  {['ACH','VTE','BQ','CAI','OD','DON','SUB'].map(j => (
                    <MenuItem key={j} value={j}>{j}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Référence"
                  value={form.reference || ''}
                  onChange={e => setForm({ ...form, reference: e.target.value })}
                  placeholder="Ex: ENT2025-00001"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button component="label" variant="outlined" startIcon={<UploadFile />}>
                  Importer PJ
                  <input hidden type="file" accept="application/pdf,image/*" onChange={e => setPjFile(e.target.files?.[0] || null)} />
                </Button>
                {pjFile && (
                  <Typography variant="caption" sx={{ ml: 1 }}>{pjFile.name}</Typography>
                )}
                <Button sx={{ ml: 1 }} variant="text" startIcon={<AutoAwesome />} onClick={() => alert('Assistant IA: lecture PJ et proposition (à intégrer backend/IA).')}>Assistant IA</Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Libellé"
                  value={form.libelle}
                  onChange={e => setForm({ ...form, libelle: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Lignes</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Compte</TableCell>
                      <TableCell>Libellé</TableCell>
                      <TableCell>Débit</TableCell>
                      <TableCell>Crédit</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.lignes.map((l, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            size="small"
                            value={l.numero_compte}
                            onChange={e => updateLigne(idx, { numero_compte: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={l.libelle}
                            onChange={e => updateLigne(idx, { libelle: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ step: '0.01' }}
                            value={l.debit}
                            onChange={e => updateLigne(idx, { debit: Number(e.target.value) })}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ step: '0.01' }}
                            value={l.credit}
                            onChange={e => updateLigne(idx, { credit: Number(e.target.value) })}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => removeLigne(idx)}><Delete fontSize="small"/></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Button onClick={addLigne}>Ajouter une ligne</Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Montant total"
                  value={form.montant_total}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Statut"
                  value={form.statut}
                  onChange={e => setForm({ ...form, statut: e.target.value as any })}
                >
                  {['brouillard','valide','annule'].map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button fullWidth variant="outlined" startIcon={<PictureAsPdf />} onClick={async () => {
                  try {
                    const pdfDoc = await PDFDocument.create();
                    const page = pdfDoc.addPage([595, 842]);
                    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
                    const { width, height } = page.getSize();
                    const draw = (text: string, x: number, y: number, size = 10) => page.drawText(text, { x, y, size, font });
                    draw('FICHE D\'IMPUTATION', 200, height - 50, 14);
                    draw(`Référence: ${form.reference || ''}`, 40, height - 90);
                    draw(`Date: ${form.date_ecriture}`, 40, height - 110);
                    draw(`Journal: ${form.journal}`, 40, height - 130);
                    draw(`Libellé: ${form.libelle}`, 40, height - 150);
                    let y = height - 180;
                    form.lignes.forEach((l, i) => {
                      draw(`${i+1}. ${l.numero_compte} - ${l.libelle}  D: ${l.debit}  C: ${l.credit}`, 40, y);
                      y -= 16;
                    });
                    const bytes = await pdfDoc.save();
                    const blob = new Blob([bytes], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = `Fiche_Imputation_${form.reference || 'ref'}.pdf`; a.click(); URL.revokeObjectURL(url);
                  } catch (e) {
                    alert('Erreur génération fiche d\'imputation');
                  }
                }}>Générer fiche d'imputation</Button>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={() => setOpen(false)}>Annuler</Button>
              <Button variant="contained" onClick={upsert}>{editing ? 'Mettre à jour' : 'Enregistrer'}</Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Ecritures;